import * as React from 'react';
import ApiClient from '../../Services/ApiClient';
import '../../style/styles.css';
import { useResources } from '../../store/resourcesProvider';
import AuthContext from '../../store/authContext';
import { useAlert } from '../ui/alertProvider';

interface LeadResult {
  id: number;
  name?: string;
  email?: string;
  phone?: string;
  createdAt?: string;
  statusLabel?: string;
  leadStatusId?: number;
}

import '../../style/leads.css';

const LeadsPage: React.FC = () => {
  const resources = useResources();
  const [leads, setLeads] = React.useState<LeadResult[]>([]);
  const [page, setPage] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [total, setTotal] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);

  const auth = React.useContext(AuthContext) as any;
  const { showAlert } = useAlert();
  const { showSnack } = useAlert() as any;

  React.useEffect(() => { loadLeads(page); }, [page, pageSize]);

  const loadLeads = async (p: number) => {
    setLoading(true);
    try {
      // include current siteId when available so admins see site-specific leads by default
      const siteId = resources?.siteId;
      const siteQs = siteId ? `&siteId=${siteId}` : '';
      const data: any = await ApiClient.get(`/api/leads/paged?page=${p}&pageSize=${pageSize}${siteQs}`);
      setLeads(data?.leads || data?.Leads || []);
      setTotal(data?.totalCount ?? data?.TotalCount ?? 0);
    } catch (ex) {
      console.error('Failed to load leads', ex);
    } finally { setLoading(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Lead status options
  const statusOptions = [
    { id: 1, label: resources.get('Lead.Status.New', 'New') },
    { id: 2, label: resources.get('Lead.Status.Contacted', 'Contacted') },
    { id: 3, label: resources.get('Lead.Status.Qualified', 'Qualified') },
    { id: 4, label: resources.get('Lead.Status.Closed', 'Closed') },
    { id: 5, label: resources.get('Lead.Status.Lost', 'Lost') }
  ];

  const [selectedLead, setSelectedLead] = React.useState<LeadResult | null>(null);

  // State for confirm modal when changing status
  const [confirmStatusOpen, setConfirmStatusOpen] = React.useState(false);
  const [pendingStatus, setPendingStatus] = React.useState<{ leadId: number; statusId: number; prev?: number } | null>(null);

  const requestStatusChange = (leadId: number, statusId: number, prev?: number) => {
    // show snackbar with Undo action
    setPendingStatus({ leadId, statusId, prev });
    showSnack && showSnack(resources.get('Lead.ConfirmStatusChange', 'Change status?'), resources.get('Undo','Undo'), async () => {
      // revert action via server if possible
      if (prev !== undefined && prev !== null) {
        try { await ApiClient.post(`/api/leads/${leadId}/status/revert`, { PreviousStatus: prev, AuditId: null }); showAlert(resources.get('Lead.StatusReverted','Status reverted'),'success'); loadLeads(page); }
        catch { showAlert(resources.get('Lead.StatusRevertFailed','Failed to revert status'),'error'); }
      }
    });
    // also perform the requested change immediately
    performStatusChange();
  };

  const updateStatus = (leadId: number, statusId: number) => {
    // find previous status from current leads list
    const lead = leads.find(l => l.id === leadId);
    const prev = lead?.leadStatusId;
    requestStatusChange(leadId, statusId, prev);
  };

  const performStatusChange = async () => {
    if (!pendingStatus) return;
    const { leadId, statusId, prev } = pendingStatus;
    setConfirmStatusOpen(false);
    try {
      await ApiClient.put(`/api/leads/${leadId}/status`, { Status: statusId });
      // audit the change
      try {
        await ApiClient.post('/api/audit/logs', { Action: 'LeadStatusChanged', Target: String(leadId), Details: `Status set to ${statusId}`, ActorEmail: (auth && auth.userId) ? String((auth as any).userId) : undefined });
      } catch {}
      showAlert(resources.get('Lead.StatusUpdated','Status updated'), 'success');
      // refresh current page
      loadLeads(page);
      // Undo handled via snackbar action
    } catch (err) {
      console.error('Failed to update status', err);
      showAlert(resources.get('Lead.StatusUpdateFailed','Failed to update status'), 'error');
    } finally {
      setPendingStatus(null);
    }
  };

  return (
    <div className="items-admin">
      <h2 className="items-title">לידים</h2>
      <div className="table-controls">
        <div style={{marginLeft:'auto', display:'flex', gap:8}}>
          <button className="btn" onClick={()=>setPage(Math.max(1,page-1))}>הקודם</button>
          <span>{page} / {totalPages}</span>
          <button className="btn" onClick={()=>setPage(Math.min(totalPages,page+1))}>הבא</button>
          <select value={pageSize} onChange={e=>{ setPageSize(parseInt(e.target.value)); setPage(1); }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>
      </div>

      <div className="leads-grid">
        {leads.map(l => (
          <div className="lead-card" key={l.id}>
            <div className="lead-main">
              <div className="lead-main-left">
                <div style={{fontWeight:600}}>{l.name}</div>
                <div className="lead-row small">{l.createdAt}</div>
              </div>
              <div className="lead-main-actions">
                <button className="btn" onClick={() => setSelectedLead(l)}>פרטים</button>
              </div>
            </div>
            <div className="lead-row">{l.email}</div>
            <div className="lead-row">{l.phone}</div>
            <div className="lead-row small">{l.createdAt}</div>
            <div className="lead-row status">{l.statusLabel}</div>
          </div>
        ))}
      </div>
      {loading && <div className="spinner"/>}
      {/* Details modal for selected lead */}
      {selectedLead && (
        <div className="modal-overlay" onClick={() => setSelectedLead(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedLead(null)}>×</button>
            <h3>{selectedLead.name}</h3>
            <div style={{marginTop:8}}>
              <div><strong>Email:</strong> {selectedLead.email}</div>
              <div><strong>Phone:</strong> {selectedLead.phone}</div>
              <div style={{marginTop:8}}>
                <label>Change status:</label>
                <select defaultValue={selectedLead.leadStatusId ?? 1} onChange={e => updateStatus(selectedLead.id, parseInt(e.target.value))}>
                  {statusOptions.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
